import * as React from "react"
import { Clock } from "lucide-react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/Components/ui/select"
import { cn } from "@/lib/utils"

export function TimePicker({ 
  value, 
  onChange, 
  className,
  disabled,
  ...props 
}) {
  const [hours, setHours] = React.useState("09")
  const [minutes, setMinutes] = React.useState("00")
  const [period, setPeriod] = React.useState("AM")

  // Parse 24-hour format (HH:mm) to 12-hour format
  React.useEffect(() => {
    if (value) {
      const [h, m] = value.split(":").map(Number)
      if (!isNaN(h) && !isNaN(m)) {
        let hour12 = h % 12
        if (hour12 === 0) hour12 = 12
        setHours(String(hour12).padStart(2, "0"))
        setMinutes(String(m).padStart(2, "0"))
        setPeriod(h >= 12 ? "PM" : "AM")
      }
    }
  }, [value])

  const updateTime = (newHours, newMinutes, newPeriod) => {
    setHours(newHours)
    setMinutes(newMinutes)
    setPeriod(newPeriod)
    
    // Convert to 24-hour format
    let hour24 = parseInt(newHours)
    if (newPeriod === "PM" && hour24 !== 12) {
      hour24 += 12
    } else if (newPeriod === "AM" && hour24 === 12) {
      hour24 = 0
    }
    
    const time24 = `${String(hour24).padStart(2, "0")}:${newMinutes}`
    onChange?.(time24)
  }

  const hourOptions = Array.from({ length: 12 }, (_, i) => {
    const hour = i + 1
    return String(hour).padStart(2, "0")
  })

  const minuteOptions = Array.from({ length: 60 }, (_, i) => {
    return String(i).padStart(2, "0")
  })

  return (
    <div className={cn("flex items-center gap-2", className)} {...props}>
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Clock className="h-4 w-4" />
      </div>
      <div className="flex items-center gap-1.5">
        <Select
          value={hours}
          onValueChange={(val) => updateTime(val, minutes, period)}
          disabled={disabled}
        >
          <SelectTrigger className="w-[70px]">
            <SelectValue placeholder="HH" />
          </SelectTrigger>
          <SelectContent>
            {hourOptions.map((hour) => (
              <SelectItem key={hour} value={hour}>
                {hour}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-muted-foreground">:</span>
        <Select
          value={minutes}
          onValueChange={(val) => updateTime(hours, val, period)}
          disabled={disabled}
        >
          <SelectTrigger className="w-[70px]">
            <SelectValue placeholder="MM" />
          </SelectTrigger>
          <SelectContent>
            {minuteOptions.map((minute) => (
              <SelectItem key={minute} value={minute}>
                {minute}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={period}
          onValueChange={(val) => updateTime(hours, minutes, val)}
          disabled={disabled}
        >
          <SelectTrigger className="w-[70px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="AM">AM</SelectItem>
            <SelectItem value="PM">PM</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
